import { isEqual } from 'lodash';
import { useContext, useMemo } from 'react';

import { DataTableContext } from 'components/DataTable';
import {
  ASSET_MAKE_MODEL_PAIRS_KEY,
  COMPONENT_MAKE_MODEL_PAIRS_KEY,
  decodeMakeModelPairs,
} from 'components/DataTable/customFilters/AssetFilter/MakeModelNestedFilter/utils';
import { getMultiFilterValueForKey } from 'components/DataTable/utils';
import {
  useGetBladeLengthsForBladesQuery,
  useGetBladeMakeModelLengthsQuery,
  useSiteBladeMakeModelsQuery,
  useTurbineBladeMakeModelsQuery,
} from 'types/atlas-graphql';

import {
  type PlotBladeLengths,
  type PlotBladeMakeModel,
  getCandidateMakeModels,
  getPlotBladeLengths,
  getUniqueMakeModels,
} from './utils';

export type PlotBladeSet = PlotBladeLengths & {
  makeModels: PlotBladeMakeModel[];
  hasPlotFilters: boolean;
  loading: boolean;
};

type UsePlotBladeSetProps = {
  skip: boolean;
  assetIds?: string[];
  representativeBlade?: PlotBladeMakeModel;
  isSiteDetailsPage: boolean;
  isTurbineDetailsPage: boolean;
  isBladeDetailsPage: boolean;
};

/**
 * Resolves the Blade Make/Models a damage plot would cover and the length range to plot them over.
 * Page-level Make/Models (Site/Turbine descendants, or the Blade itself) take precedence while
 * plottable on their own; otherwise the table's asset filters narrow the set to something that is.
 */
export const usePlotBladeSet = ({
  skip,
  assetIds,
  representativeBlade,
  isSiteDetailsPage,
  isTurbineDetailsPage,
  isBladeDetailsPage,
}: UsePlotBladeSetProps): PlotBladeSet => {
  const { filteredInfo } = useContext(DataTableContext);

  const { makes, models, pairs, bladeIds } = useMemo(() => {
    const assetFilters = filteredInfo?.asset ?? [];
    const [
      assetType,
      assetMake,
      assetModel,
      assetMakeModelPairs,
      componentType,
      componentMake,
      componentModel,
      componentMakeModelPairs,
      assetId,
      componentId,
    ] = [
      'assetType',
      'assetMake',
      'assetModel',
      ASSET_MAKE_MODEL_PAIRS_KEY,
      'componentType',
      'componentMake',
      'componentModel',
      COMPONENT_MAKE_MODEL_PAIRS_KEY,
      'assetId',
      'componentId',
    ].map(k => getMultiFilterValueForKey(assetFilters, k));

    const bladesFilteredAsAssets = isEqual(assetType, ['blade']);
    const bladesFilteredAsComponents = isEqual(componentType, ['blade']);

    // Asset ids are stored as `<assetId>:<siteId>`
    const filteredBladeIds = (
      bladesFilteredAsAssets ? assetId : bladesFilteredAsComponents ? componentId : []
    )?.map((id: string) => id.split(':')[0]);

    return {
      makes: bladesFilteredAsAssets
        ? assetMake
        : bladesFilteredAsComponents
          ? componentMake
          : undefined,
      models: bladesFilteredAsAssets
        ? assetModel
        : bladesFilteredAsComponents
          ? componentModel
          : undefined,
      pairs: decodeMakeModelPairs(
        bladesFilteredAsAssets
          ? assetMakeModelPairs
          : bladesFilteredAsComponents
            ? componentMakeModelPairs
            : undefined
      ),
      bladeIds: filteredBladeIds?.length
        ? filteredBladeIds
        : isBladeDetailsPage
          ? assetIds ?? []
          : [],
    };
  }, [filteredInfo?.asset, isBladeDetailsPage, assetIds]);

  // Either side is enough: a Make with no model list still pins the plot to that Make's Blades
  const hasMakeModelFilter = !!pairs.length || !!makes?.length || !!models?.length;
  const needsMakeModelLengths = hasMakeModelFilter || !!representativeBlade;

  const { data: siteBladeMakeModelsData, loading: siteBladeMakeModelsLoading } =
    useSiteBladeMakeModelsQuery({
      variables: { siteId: assetIds?.[0] ?? '' },
      skip: skip || !isSiteDetailsPage,
    });
  const { data: turbineBladeMakeModelsData, loading: turbineBladeMakeModelsLoading } =
    useTurbineBladeMakeModelsQuery({
      variables: { turbineId: assetIds?.[0] ?? '' },
      skip: skip || !isTurbineDetailsPage,
    });
  const { data: makeModelLengthsData, loading: makeModelLengthsLoading } =
    useGetBladeMakeModelLengthsQuery({ skip: skip || !needsMakeModelLengths });
  const { data: bladeLengthsData, loading: bladeLengthsLoading } = useGetBladeLengthsForBladesQuery(
    {
      // Bounded by the ids asked for, so the server's default page size cannot truncate the set
      variables: { filterBy: [{ key: 'assetIds', values: bladeIds }], limit: bladeIds.length },
      skip: skip || hasMakeModelFilter || !bladeIds.length,
    }
  );

  const pageMakeModels: PlotBladeMakeModel[] = useMemo(() => {
    if (isSiteDetailsPage) {
      return getUniqueMakeModels(siteBladeMakeModelsData?.siteBladeMakeModels ?? []);
    }
    if (isTurbineDetailsPage) {
      return getUniqueMakeModels(turbineBladeMakeModelsData?.turbineBladeMakeModels ?? []);
    }
    return [];
  }, [
    isSiteDetailsPage,
    isTurbineDetailsPage,
    siteBladeMakeModelsData?.siteBladeMakeModels,
    turbineBladeMakeModelsData?.turbineBladeMakeModels,
  ]);

  const filteredMakeModels: PlotBladeMakeModel[] = useMemo(() => {
    if (hasMakeModelFilter) {
      return getCandidateMakeModels({
        makes,
        models,
        pairs,
        configuredMakeModels: makeModelLengthsData?.bladeCrossSectionWidthsForAllMakeModels ?? [],
      });
    }

    if (bladeIds.length) {
      const filteredBlades = bladeLengthsData?.assetsWithMetadata;
      // A Blade with no Make/Model data, or one the server left out of the page, keeps an empty
      // entry so it counts as unknown length and blocks the plot rather than skewing its axis.
      const bladeMakeModels = getUniqueMakeModels(
        (filteredBlades?.items ?? []).map(asset =>
          'bladeMakeModel' in asset ? asset.bladeMakeModel ?? {} : {}
        )
      );

      return (filteredBlades?.totalCount ?? 0) > (filteredBlades?.items.length ?? 0)
        ? [...bladeMakeModels, {}]
        : bladeMakeModels;
    }

    return [];
  }, [
    hasMakeModelFilter,
    makes,
    models,
    pairs,
    makeModelLengthsData?.bladeCrossSectionWidthsForAllMakeModels,
    bladeIds,
    bladeLengthsData?.assetsWithMetadata,
  ]);

  const representativeMakeModels = useMemo(
    () =>
      representativeBlade
        ? getCandidateMakeModels({
            makes: representativeBlade.make ? [representativeBlade.make] : undefined,
            models: representativeBlade.model ? [representativeBlade.model] : undefined,
            configuredMakeModels:
              makeModelLengthsData?.bladeCrossSectionWidthsForAllMakeModels ?? [],
          })
        : [],
    [representativeBlade, makeModelLengthsData?.bladeCrossSectionWidthsForAllMakeModels]
  );

  const loading =
    (isSiteDetailsPage && (!siteBladeMakeModelsData || siteBladeMakeModelsLoading)) ||
    (isTurbineDetailsPage && (!turbineBladeMakeModelsData || turbineBladeMakeModelsLoading)) ||
    (needsMakeModelLengths && (!makeModelLengthsData || makeModelLengthsLoading)) ||
    (!hasMakeModelFilter && !!bladeIds.length && (!bladeLengthsData || bladeLengthsLoading));

  return useMemo(() => {
    const makeModels =
      pageMakeModels.length && getPlotBladeLengths(pageMakeModels).canPlot
        ? pageMakeModels
        : representativeMakeModels.length
          ? representativeMakeModels
          : filteredMakeModels.length
            ? filteredMakeModels
            : pageMakeModels;

    return {
      ...getPlotBladeLengths(makeModels),
      makeModels,
      hasPlotFilters: !!representativeMakeModels.length || !!filteredMakeModels.length,
      loading,
    };
  }, [pageMakeModels, representativeMakeModels, filteredMakeModels, loading]);
};
